import React, { useEffect, useState } from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardContent from "../../components/dashboard/DashboardContent";
import useResponsive from "../../hooks/useResponsive";
import Header from "../../layout/dashboard/header";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../../components/notification";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { useMutation } from "@tanstack/react-query";
import { apiGetFacility } from "../../api/facility";
import { apiGetGeneralStats } from "../../api";
import { DashboardStats } from "../../interface/dashboard";

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  const { token } = useAuth();
  const [facility_list, setFacility_list] = useState<any>([]);
  const [filter, setFilter] = useState("");
  const [stats, setStats] = useState<DashboardStats>({
    totalApplications: 0,
    totalFacility: 0,
    totalMedics: 0,
    totalJobs: 0,
  });

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await statsRequest.mutateAsync();
        await mutation.mutateAsync({
          page: pagination_state.page,
          filter: filter,
          token: token,
        });
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state?.page]);

  useEffect(() => {
    mutation.mutate({
      page: pagination_state.page,
      filter: filter,
      token: token,
    });
  }, [pagination_state.page]);

  const mutation = useMutation({
    mutationFn: (field: { page: number; filter: string; token: string }) =>
      apiGetFacility(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setFacility_list(data.data.data);
        } else {
          setFacility_list([]);
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  const statsRequest = useMutation({
    mutationFn: () => apiGetGeneralStats(token),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setStats(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });

  return (
    <>
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: theme.palette.secondary.dark,
              }}
            >
              Dashboard
            </Typography>
          </Stack>
        </Box>
      </Header>
      <DashboardContent
        facility_list={facility_list}
        stats={stats}
        loading={mutation.isPending}
      />
    </>
  );
};

export default Dashboard;
